import React, { createContext, useContext, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import DashboardLayout from './pages/DashboardLayout';
import ConversationsPage from './pages/ConversationsPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import { AdminLang } from './i18n';
import './App.scss';

export interface AdminSession {
  id: string;
  name: string;
  phone: string;
}

const STORAGE_KEY = 'dp_admin_session';
const LANG_KEY = 'dp_admin_lang';

// ── Admin Language Context ──────────────────────────────────────────────────
interface AdminLangContextValue {
  lang: AdminLang;
  setLang: (l: AdminLang) => void;
}

export const AdminLangContext = createContext<AdminLangContextValue>({
  lang: 'en',
  setLang: () => {},
});

export function useAdminLang() {
  return useContext(AdminLangContext);
}
// ────────────────────────────────────────────────────────────────────────────

function App() {
  const [session, setSession] = useState<AdminSession | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [lang, setLangState] = useState<AdminLang>(() => {
    const saved = localStorage.getItem(LANG_KEY);
    return (saved === 'ar' || saved === 'en') ? saved : 'en';
  });

  const setLang = (l: AdminLang) => {
    localStorage.setItem(LANG_KEY, l);
    setLangState(l);
  };

  const handleLogin = (data: AdminSession) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setSession(data);
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setSession(null);
  };

  return (
    <AdminLangContext.Provider value={{ lang, setLang }}>
    <BrowserRouter>
      <Routes>
        <Route
          path="/login"
          element={
            session ? <Navigate to="/" replace /> : <LoginPage onLogin={handleLogin} />
          }
        />
        <Route
          path="/"
          element={
            session ? (
              <DashboardLayout session={session} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        >
          <Route index element={<Navigate to="/conversations" replace />} />
          <Route path="conversations" element={<ConversationsPage />} />
          <Route path="conversations/:id" element={<ConversationsPage />} />
          <Route path="knowledge-base" element={<KnowledgeBasePage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
    </AdminLangContext.Provider>
  );
}

export default App;
